<template>
  <v-list
    class="custom-attributes__container"
    :class="additionalContainerClasses"
    ref="listContainer"
  >
    <div v-if="loading">
      <v-list-item
        v-for="i in 3"
        :key="i"
        style="border: 1px solid rgba(0, 0, 0, 0.12); height: 56px"
      >
        <v-list-item-icon>
          <v-skeleton-loader style="width: 24px; height: 24px" type="button" />
        </v-list-item-icon>
        <v-list-item-content>
          <v-skeleton-loader
            style="flex-grow: 1; max-width: 120px"
            type="list-item"
          />
        </v-list-item-content>
        <v-list-item-action style="margin-left: 4px">
          <v-skeleton-loader style="width: 24px; height: 24px" type="button" />
        </v-list-item-action>
      </v-list-item>
    </div>
    <div
      class="custom-attributes__placeholder disable-sort"
      v-if="isPlaceholderVisible && !loading"
    >
      Füge ein neues Feld hinzu, um es hier zu sehen.
    </div>
    <template v-if="!loading">
      <CustomAttributesListItem
        :class="{ 'disable-sort': isAnyInProgcess }"
        :data-id="customAttribute.id"
        v-for="customAttribute in attributes"
        :key="customAttribute.id"
        :custom-attribute="customAttribute"
        @deleted="handleDeletion"
        @moved="handleMoved"
        @updated="handleUpdate"
        @update-in-progress="() => handleUpdateInProgress(customAttribute.id)"
      />
    </template>
  </v-list>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, PropType, ref, watch } from "vue"
import {
  CustomAttributeDefinition,
  CustomAttributeDefinitionAttributableTypeEnum,
} from "suus-api"
import Button from "components/UI/Button.vue"
import Header2 from "app/ui-primitives/header/Header2.vue"
import Sortable from "sortablejs"
import CustomAttributesListItem from "app/modules/custom-attributes/CustomAttributesListItem.vue"
import { useSnackbar } from "app/ui-primitives/snackbars/SnackbarProvider.vue"
import {
  CustomAttributesClient,
  LockedException,
} from "app/modules/custom-attributes/client"

export default defineComponent({
  name: "CustomAttributesList",
  components: {
    CustomAttributesListItem,
    Header2,
    Button,
  },
  props: {
    attributableType: {
      type: String as PropType<CustomAttributeDefinitionAttributableTypeEnum>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const loading = ref(true)
    const attributes = ref<CustomAttributeDefinition[]>([])

    const fetchFunction =
      props.attributableType === "PersonalRecord"
        ? CustomAttributesClient.fetchPersonalAttributes
        : CustomAttributesClient.fetchClubAttributes

    fetchFunction()
      .then((response) => {
        attributes.value = response
      })
      .catch((e) => {
        emit("fetchError")
        console.error(e)
      })
      .finally(() => {
        loading.value = false
      })

    const addAttribute = (attr: CustomAttributeDefinition) => {
      attributes.value = [...attributes.value, attr]
    }

    const { showError } = useSnackbar()
    const listContainer = ref<any | null>(null)
    onMounted(() => {
      let previousSort = null
      const sortable = new Sortable(listContainer.value.$el, {
        sort: true,
        chosenClass: "chosen",
        filter: ".disable-sort",
        dataIdAttr: "data-id",
        onStart: (_) => {
          previousSort = sortable.toArray()
        },
        onEnd: async (event) => {
          try {
            const movedAttribute = attributes.value[event.oldIndex]
            const targetAttribute = attributes.value[event.newIndex]
            attributes.value = await CustomAttributesClient.reorder(
              movedAttribute.id,
              targetAttribute.id
            )
          } catch (e) {
            if (e instanceof LockedException) {
              const movedAttribute = attributes.value[event.oldIndex]
              attributes.value = attributes.value.map((attr) => {
                if (attr.id === movedAttribute.id) {
                  return { ...attr, update_in_progress: true }
                }
                return attr
              })
              console.warn(e)
            }
            console.error(e)
            sortable.sort(previousSort)
            showError("Fehler beim Verschieben des Feldes")
          }
        },
      })
    })

    const isPlaceholderVisible = computed(() => attributes.value.length === 0)

    const additionalContainerClasses = computed(() => {
      return {
        "dropzone-visible": attributes.value.length === 0 && !loading.value,
      }
    })

    const handleDeletion = (id: number) => {
      attributes.value = attributes.value.filter((attr) => attr.id !== id)
    }

    const handleMoved = ({
      movedAttribute,
      targetList,
    }: {
      movedAttribute: CustomAttributeDefinition
      targetList: CustomAttributeDefinitionAttributableTypeEnum
    }) => {
      if (targetList === props.attributableType) {
        return
      }
      attributes.value = [
        ...attributes.value.filter((attr) => attr.id !== movedAttribute.id),
      ]
      emit("moved", { movedAttribute, targetList })
    }

    const handleUpdate = (updatedAttr: CustomAttributeDefinition) => {
      const copy = [...attributes.value]
      const indexOfUpdatedAttr = copy.findIndex(
        (attr) => attr.id === updatedAttr.id
      )
      copy[indexOfUpdatedAttr] = updatedAttr
      attributes.value = copy
    }

    const isAnyInProgcess = computed(() => {
      return attributes.value.some((attr) => attr.update_in_progress)
    })

    let interval = null

    watch(
      () => isAnyInProgcess.value,
      (newValue) => {
        if (newValue) {
          interval = setInterval(() => {
            CustomAttributesClient.fetchProgress(
              attributes.value
                .filter((attr) => attr.update_in_progress)
                .map((attr) => attr.id)
            ).then((response) => {
              attributes.value = attributes.value.map((attr) => {
                const updated = response.find((r) => r.id === attr.id)
                return {
                  ...attr,
                  update_in_progress: updated
                    ? updated.update_in_progress
                    : false,
                }
              })
            })
          }, 5000)
        } else {
          clearInterval(interval)
        }
      },
      {
        immediate: true,
      }
    )

    const handleUpdateInProgress = (id: number) => {
      attributes.value = attributes.value.map((attr) => {
        if (attr.id === id) {
          return { ...attr, update_in_progress: true }
        }
        return attr
      })
    }

    return {
      listContainer,
      additionalContainerClasses,
      isPlaceholderVisible,
      handleDeletion,
      loading,
      attributes,
      addAttribute,
      handleMoved,
      handleUpdate,
      isAnyInProgcess,
      handleUpdateInProgress,
    }
  },
})
</script>

<style scoped>
.custom-attributes__container {
  width: 100%;
}

.custom-attributes__container.dropzone-visible {
  border: 1px dashed #c5c5c5;
  border-radius: 4px;
  min-height: 56px;
}

.custom-attributes__placeholder {
  height: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.disable-sort {
  cursor: default !important;
}
</style>
